"use client";

import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Navbar, NavbarBrand, NavbarContent, Chip, Button } from "@nextui-org/react";
import { ShoppingBag, List, Filter } from "lucide-react";
import Link from "next/link";

interface ProductSummary {
  id: number;
  name: string;
  category: string;
  attributes?: {
    gorivo?: string;
    frekvencija?: string;
    faza?: string;
    emisija?: string;
    snaga?: string;
  };
}

export default function ProductsOverviewPage() {
  const searchParams = useSearchParams();
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProductSummaries() {
      try {
        setLoading(true);
        setError(null);

        // Parse category_exclude from URL
        const categoryExcludeParam = searchParams.get('category_exclude');
        const excludedCategories = categoryExcludeParam 
          ? categoryExcludeParam.split(',').map(id => parseInt(id.trim()))
          : [1178]; // Only exclude Battery systems (ID 1178)

        // Fetch first page to get product summaries
        const params = new URLSearchParams({
          consumer_key: "ck_50493b2d57ff0f3895567b202c207e1bd36dbac7",
          consumer_secret: "cs_8dd00d08612e5c652cee2b27aeaded98e032d756",
          per_page: "100",
        });

        const response = await fetch(
          `https://tehnolink.net/wp-json/wc/v3/products?${params.toString()}`,
          {
            headers: { "Content-Type": "application/json" }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }

        const productsData = await response.json();
        
        console.log('Fetched products:', productsData.length);
        console.log('Excluded categories:', excludedCategories);
        
        // Log first product structure to debug
        if (productsData.length > 0) {
     
          // Find product with ID 7416
          const product7416 = productsData.find((p: any) => p.id === 7416);
          if (product7416) {
            console.log('Product 7416 found:', product7416);
            console.log('Product 7416 attributes:', product7416.attributes);
            // Log detailed attribute structure
            product7416.attributes.forEach((attr: any, i: number) => {
              console.log(`Attribute ${i} (${attr.name}):`, {
                id: attr.id,
                name: attr.name,
                slug: attr.slug,
                options: attr.options,
                value: attr.value
              });
            });
            console.log('Product 7416 meta_data:', product7416.meta_data?.map((m: any) => ({key: m.key, value: m.value})));
            console.log('Product 7416 description:', product7416.description);
            console.log('Product 7416 short_description:', product7416.short_description);
          } else {
            console.log('Product 7416 not found in this batch');
            // Log first few products to see their attributes
            console.log('First 3 products attributes:');
            productsData.slice(0, 3).forEach((p: any, i: number) => {
              console.log(`Product ${p.id} attributes:`, p.attributes);
            });
          }
        }
        
        // Create simple summary for each product and filter out excluded categories
        const summaries: ProductSummary[] = productsData
          .filter((product: any) => {
            // Check if product has any excluded category
            if (product.categories && product.categories.length > 0) {
              const hasExcludedCategory = product.categories.some((cat: any) => 
                excludedCategories.includes(cat.id)
              );
              return !hasExcludedCategory;
            }
            return true; // Include products with no categories
          })
          .map((product: any) => {
            // Extract attributes from WooCommerce attributes array
            const attributes: any = {};
            if (product.attributes && product.attributes.length > 0) {
              product.attributes.forEach((attr: any) => {
                // Map WooCommerce attribute names to our display names
                if (attr.name === 'fuel') attributes.gorivo = attr.options?.[0] || attr.value;
                if (attr.name === 'frequency') attributes.frekvencija = attr.options?.[0] || attr.value;
                if (attr.name === 'Phase') attributes.faza = attr.options?.[0] || attr.value;
                if (attr.name === 'Emission') attributes.emisija = attr.options?.[0] || attr.value;
                if (attr.name === 'Power (PRP / ESP)') attributes.snaga = attr.options?.[0] || attr.value;
              });
            }

            return {
              id: product.id,
              name: product.name,
              category: product.categories && product.categories.length > 0 
                ? product.categories[0].name 
                : 'Uncategorized',
              attributes
            };
          });

        console.log('Filtered products:', summaries.length);
        
        // Collect all unique categories to find Battery systems ID
        const allCategories = new Map<number, string>();
        productsData.forEach((product: any) => {
          if (product.categories && product.categories.length > 0) {
            product.categories.forEach((cat: any) => {
              allCategories.set(cat.id, cat.name);
            });
          }
        });
        console.log('All categories found:', Array.from(allCategories.entries()));

        setProducts(summaries);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch products");
      } finally {
        setLoading(false);
      }
    }

    fetchProductSummaries();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Navbar position="sticky" className="bg-white shadow-sm">
        <NavbarBrand>
          <ShoppingBag className="w-6 h-6 text-primary mr-2" />
          <p className="font-bold text-xl">Product Catalog</p>
        </NavbarBrand>
        <NavbarContent justify="end">
          <Button
            as={Link}
            href="/products"
            variant="flat"
            startContent={<Filter className="w-4 h-4" />}
          >
            Filter Products
          </Button>
          <Button
            as={Link}
            href="/products/search"
            color="primary"
            startContent={<List className="w-4 h-4" />}
            className="ml-2"
          >
            Search Products
          </Button>
        </NavbarContent>
      </Navbar>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Product Overview
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Browse our complete catalog of {products.length} products
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading products...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-semibold">Error: {error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Product List ({products.length} items)
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((product) => (
                <div 
                  key={product.id} 
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow hover:border-primary-300"
                >
                  <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between mb-2">
                    <Chip 
                      size="sm" 
                      variant="flat" 
                      color="primary"
                    >
                      {product.category}
                    </Chip>
                    <span className="text-sm text-gray-500">
                      ID: {product.id}
                    </span>
                  </div>
                  
                  {/* Product Attributes */}
                  {product.attributes && (
                    <div className="text-sm text-gray-600 space-y-1">
                      {product.attributes.gorivo && (
                        <div><strong>Fuel:</strong> {product.attributes.gorivo}</div>
                      )}
                      {product.attributes.frekvencija && (
                        <div><strong>Frequency:</strong> {product.attributes.frekvencija}</div>
                      )}
                      {product.attributes.faza && (
                        <div><strong>Phase:</strong> {product.attributes.faza}</div>
                      )}
                      {product.attributes.emisija && (
                        <div><strong>Emission:</strong> {product.attributes.emisija}</div>
                      )}
                      {product.attributes.snaga && (
                        <div><strong>Power:</strong> {product.attributes.snaga}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
